/**
 * JSON parsing utilities and streaming utility functions for streaming data.
 */

/**
 * Type guard for OpenAI-style streaming chunks.
 *
 * @param chunk The chunk to check
 * @returns True if the chunk has OpenAI format with choices array containing delta objects
 */
export const isOpenAIChunk = (
  chunk: unknown
): chunk is {
  choices: Array<{
    delta: {
      content?: string;
    };
  }>;
} => {
  return (
    typeof chunk === 'object' &&
    chunk !== null &&
    'choices' in chunk &&
    Array.isArray((chunk as any).choices) &&
    (chunk as any).choices.length > 0 &&
    'delta' in (chunk as any).choices[0]
  );
};

/**
 * Type guard for simple content chunks with a direct 'content' property.
 *
 * @param chunk The chunk to check
 * @returns True if the chunk has a 'content' property with a string value
 */
export const isContentChunk = (
  chunk: unknown
): chunk is {
  content: string;
} => {
  return (
    typeof chunk === 'object' &&
    chunk !== null &&
    'content' in chunk &&
    typeof (chunk as any).content === 'string'
  );
};

/**
 * Check if a value is a ResponseFormat object (has a 'type' property).
 *
 * @param value The value to check
 * @returns True if the value is an object with a 'type' property
 */
export const isResponseFormat = (value: unknown): value is {type: string} => {
  return typeof value === 'object' && value !== null && 'type' in value;
};

/**
 * Extract content from a streaming chunk.
 *
 * @param chunk The streaming chunk to extract content from
 * @returns The extracted content string, or null if no content found
 */
export const extractContent = (chunk: unknown): string | null => {
  if (isOpenAIChunk(chunk)) {
    return chunk.choices[0]?.delta?.content || null;
  }

  if (isContentChunk(chunk)) {
    return chunk.content;
  }

  if (typeof chunk === 'string') {
    return chunk;
  }

  return null;
};

/**
 * Filter out undefined values from an object.
 *
 * Creates a new object containing only properties with defined values.
 *
 * @param obj The object to filter
 * @returns A new object with undefined values removed
 */
export const filterUndefined = <T extends Record<string, any>>(
  obj: T
): Record<string, any> => {
  return Object.fromEntries(
    Object.entries(obj).filter(([_, value]) => value !== undefined)
  );
};

/**
 * Parse partial JSON arrays from streaming content.
 *
 * Handles incomplete JSON arrays that are being streamed in chunks,
 * attempting to extract complete items as they become available.
 *
 * @param buffer The accumulated string buffer from streaming
 * @returns Array of successfully parsed items
 */
export function parsePartialArray(buffer: string): any[] {
  const trimmed = buffer.trim();
  if (!trimmed.startsWith('[')) {
    return [];
  }

  const items: any[] = [];

  // Try to parse as complete JSON first
  try {
    const parsed = JSON.parse(trimmed);
    if (Array.isArray(parsed)) {
      return parsed;
    }
  } catch {
    // Continue with partial parsing
  }

  // Simple parsing for partial arrays
  // Remove opening bracket and any trailing incomplete content
  let content = trimmed.slice(1);

  // Split by commas at the top level (not inside objects/arrays)
  let depth = 0;
  let inString = false;
  let escape = false;
  let current = '';

  for (let i = 0; i < content.length; i++) {
    const char = content[i];

    if (escape) {
      current += char;
      escape = false;
      continue;
    }

    if (char === '\\') {
      current += char;
      escape = true;
      continue;
    }

    if (char === '"' && !escape) {
      current += char;
      inString = !inString;
      continue;
    }

    if (!inString) {
      if (char === '{' || char === '[') {
        depth++;
        current += char;
      } else if (char === '}' || char === ']') {
        depth--;
        current += char;

        // If we're back at depth 0 and have content, try to parse it
        if (depth === 0 && current.trim()) {
          try {
            const item = JSON.parse(current.trim());
            items.push(item);
            current = '';
          } catch {
            // Item is incomplete, keep accumulating
          }
        }
      } else if (char === ',' && depth === 0) {
        // Try to parse what we have so far
        if (current.trim()) {
          try {
            const item = JSON.parse(current.trim());
            items.push(item);
            current = '';
          } catch {
            // Item is incomplete, keep accumulating
          }
        }
      } else {
        current += char;
      }
    } else {
      current += char;
    }
  }

  // Try to parse any remaining content
  if (current.trim()) {
    try {
      const item = JSON.parse(current.trim());
      items.push(item);
    } catch {
      // Incomplete item, ignore
    }
  }

  return items;
}
